/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import { getWeekRange } from "../../services/dateService";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import {
  groupByWeeks,
  timeseriesToQuarterly,
} from "../../services/arrayService";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useQualityInput } from "../../store/useQualityInput";
import { group } from "console";

// Extend dayjs with weekOfYear plugin
dayjs.extend(weekOfYear);

export const PlasticQualityStats = React.memo(() => {
  /** Refs */
  const snapshotRef = useRef(null);
  /** Hooks */
  const { plasticQualityStats, getPlasticQualityStats } = useQualityInput();
  const [_, takeScreenshot] = useScreenshot();
  /** State */
  const [period, setPeriod] = useState<
    "Daily" | "Weekly" | "Monthly" | "Quarterly" | "Yearly"
  >("Monthly");
  const [weeklyRange, setWeeklyRange] = useState(new Date());
  const [monthRange, setMonthRange] = useState(new Date());
  const [yearRange, setYearRange] = useState(new Date());
  /** Constants */
  const { startOfWeek, endOfWeek } = getWeekRange(weeklyRange);

  /** Callbacks */
  const downloadScreenshot = () => {
    takeScreenshot(snapshotRef.current).then((image: any) => {
      // Create a link to download the image
      const link = document.createElement("a");
      link.href = image;
      link.download = "plastic_pollution_trending_line.png"; // Set the default file name
      link.click(); // Programmatically click the link to trigger download
    });
  };

  const getLastWeek = () =>
    setWeeklyRange(new Date(weeklyRange.setDate(weeklyRange.getDate() - 7)));
  const getNextWeek = () =>
    setWeeklyRange(new Date(weeklyRange.setDate(weeklyRange.getDate() + 7)));

  const getLastMonth = () =>
    setMonthRange(dayjs(monthRange).subtract(1, "month").toDate());
  const getNextMonth = () =>
    setMonthRange(dayjs(monthRange).add(1, "month").toDate());

  const getLastYear = () =>
    setYearRange(dayjs(yearRange).subtract(1, "year").toDate());
  const getNextYear = () =>
    setYearRange(dayjs(yearRange).add(1, "year").toDate());

  const getMonths = (date: Date) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const currentMonth = date.getMonth(); // getMonth() returns 0 for January, 11 for December

    return monthNames.slice(0, currentMonth + 1);
  };

  const monthlyTickAmount = useMemo(() => {
    const months = getMonths(yearRange);
    if (new Date().getFullYear() === yearRange.getFullYear()) {
      return months?.length + 1;
    }
    return 11;
  }, [yearRange]);

  /** Effects */
  useEffect(() => {
    if (period === "Daily")
      getPlasticQualityStats(
        weeklyRange,
        period?.toLowerCase() as "daily" | "weekly" | "monthly" | "yearly"
      );
    if (period === "Weekly")
      getPlasticQualityStats(
        monthRange,
        period?.toLowerCase() as "daily" | "weekly" | "monthly" | "yearly"
      );
    if (period === "Monthly" || period === "Quarterly")
      getPlasticQualityStats(
        yearRange,
        "monthly" as "daily" | "weekly" | "monthly" | "yearly"
      );
    if (period === "Yearly") {
      getPlasticQualityStats(
        new Date(),
        period?.toLowerCase() as "daily" | "weekly" | "monthly" | "yearly"
      );
    }
  }, [period, weeklyRange, monthRange, yearRange]);

  return (
    <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-10">
      <div className="flex flex-col sm:flex-row w-full items-start justify-between">
        <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-2 sm:mb-12">
          {/* Volumes of the week */}
          Plastic quality
        </p>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end">
          {period === "Daily" ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
              <PrimaryButton size="sm" onClick={getLastWeek} label="Prev" />
              <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                {startOfWeek.toDateString()} - {endOfWeek.toDateString()}
              </div>
              <PrimaryButton
                size="sm"
                disabled={endOfWeek > new Date()}
                onClick={getNextWeek}
                label="Next"
              />
            </div>
          ) : null}
          {period === "Weekly" ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
              <PrimaryButton size="sm" onClick={getLastMonth} label="Prev" />
              <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                {dayjs(monthRange).format("MMMM YYYY")}
              </div>
              <PrimaryButton
                size="sm"
                disabled={dayjs(monthRange).add(1, "month").isAfter(dayjs())}
                onClick={getNextMonth}
                label="Next"
              />
            </div>
          ) : null}
          {period === "Monthly" || period === "Quarterly" ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
              <PrimaryButton size="sm" onClick={getLastYear} label="Prev" />
              <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                {dayjs(yearRange).format("YYYY")}
              </div>
              <PrimaryButton
                size="sm"
                disabled={dayjs(yearRange).add(1, "year").isAfter(dayjs())}
                onClick={getNextYear}
                label="Next"
              />
            </div>
          ) : null}
          <Menu>
            <MenuHandler>
              <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                {period}
                <ChevronDown className="ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              <MenuItem
                disabled={period === "Daily"}
                onClick={() => setPeriod("Daily")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Daily
              </MenuItem>
              <MenuItem
                disabled={period === "Weekly"}
                onClick={() => setPeriod("Weekly")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Weekly
              </MenuItem>
              <MenuItem
                disabled={period === "Monthly"}
                onClick={() => setPeriod("Monthly")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Monthly
              </MenuItem>
              <MenuItem
                disabled={period === "Quarterly"}
                onClick={() => setPeriod("Quarterly")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Quarterly
              </MenuItem>
              <MenuItem
                disabled={period === "Yearly"}
                onClick={() => setPeriod("Yearly")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Yearly
              </MenuItem>
            </MenuList>
          </Menu>
          <div className="ml-0 sm:ml-2" />
          <PrimaryButton
            size="sm"
            onClick={downloadScreenshot}
            label="Download image"
          />
        </div>
      </div>
      <div ref={snapshotRef}>
        {plasticQualityStats && period === "Daily" ? (
          <Chart
            // @ts-ignore
            options={{
              ...options,
              xaxis: {
                type: "datetime",
                tickAmount: 6,
                labels: {
                  // @ts-ignore
                  formatter: function (value: number) {
                    return dayjs(value).format("DD MMM");
                  },
                },
              },
            }}
            series={plasticQualityStats}
            type="bar"
            height={300}
          />
        ) : null}
        {plasticQualityStats && period === "Weekly" ? (
          <Chart
            // @ts-ignore
            options={{
              ...options,
              xaxis: {
                tickAmount:
                  groupByWeeks(plasticQualityStats)?.[0]?.data?.length - 1,
                labels: {
                  // @ts-ignore
                  formatter: function (_, timestamp: number) {
                    // Calculate the week number for the current timestamp

                    return `Week ${timestamp}`;
                  },
                },
              },
            }}
            series={groupByWeeks(plasticQualityStats)}
            type="bar"
            height={300}
          />
        ) : null}
        {plasticQualityStats && period === "Monthly" ? (
          <Chart
            options={{
              ...options,
              xaxis: {
                // Only show 1 tick when there's only one month
                tickAmount: monthlyTickAmount,
                labels: {
                  // @ts-ignore
                  formatter: function (value: number) {
                    const thisMonth = dayjs(value).add(5, "day").format("MMM");
                    const currentMonth = dayjs(new Date()).format("MMM");
                    const isSameYear =
                      yearRange.getFullYear() === new Date().getFullYear();
                    if (currentMonth === thisMonth && isSameYear) {
                      return "Past 30 days";
                    }
                    return dayjs(value).add(5, "day").format("MMM");
                  },
                },
              },
            }}
            series={plasticQualityStats}
            type="bar"
            height={300}
          />
        ) : null}
        {plasticQualityStats && period === "Quarterly" ? (
          <Chart
            options={{
              ...options,
              xaxis: {
                // Only show 1 tick when there's only one month
                tickAmount: 3,
                labels: {
                  // @ts-ignore
                  formatter: function (value: number) {
                    return `Q${value}`;
                  },
                },
              },
            }}
            series={timeseriesToQuarterly((plasticQualityStats as any) ?? [])}
            type="bar"
            height={300}
          />
        ) : null}
        {plasticQualityStats && period === "Yearly" ? (
          <Chart
            // @ts-ignore
            options={{
              ...options,
              xaxis: {
                tickAmount: 3,
                labels: {
                  // @ts-ignore
                  formatter: function (value: number) {
                    return dayjs(value).format("YYYY");
                  },
                },
              },
            }}
            series={plasticQualityStats}
            type="bar"
            height={300}
          />
        ) : null}
      </div>
    </div>
  );
});

const options = {
  chart: {
    type: "bar",
    height: 300,
    stacked: true,
    stackType: "100%",
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
  },
  fill: {
    opacity: 1,
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        legend: {
          position: "bottom",
          offsetX: -10,
          offsetY: 0,
        },
      },
    },
  ],
  dataLabels: {
    enabled: false, // Enable data labels
    // formatter: function (val: number) {
    //   return val.toFixed(1) + "%";
    // },
    // style: {
    //   fontSize: "12px",
    // },
    // offsetY: -7,
  },
  tooltip: {
    theme: "dark",
    y: {
      formatter: (value: number) => {
        return `${value}%`;
      },
    },
  },
  yaxis: {
    labels: {
      formatter: function (val: number) {
        return val + "%";
      },
    },
    title: {
      text: "Percentage",
      style: {
        fontSize: "14px",
        fontWeight: 500,
        fontFamily: "satoshi",
      },
    },
  },
  grid: {
    strokeDashArray: 5,
  },
  legend: {
    position: "right",
    formatter: function (seriesName: string) {
      const qualities = {
        "1": "Bad",
        "2": "Not so bad",
        "3": "Fine",
        "4": "Good",
        "5": "Excellent",
      };
      // @ts-ignore
      return `${seriesName}: ${qualities[seriesName]}`;
    },
  },
  markers: {
    size: 1,
  },
  colors: [
    "#23C16B", // Green
    "#FFB323", // Yellow
    "#48A7F8", // Blue
    "#FF3333", // Red
    "#FF8800", // Orange
    "#9259FF", // Purple
    "#FF4DA6", // Pink
    "#33FFE6", // Teal
    "#808080", // Gray
    "#D17A50", // Brown
    "#FF7F50", // Coral
    "#8A2BE2", // BlueViolet
    "#5F9EA0", // CadetBlue
    "#DC143C", // Crimson
    "#00FFFF", // Cyan
    "#FF1493", // DeepPink
    "#696969", // DimGray
    "#B8860B", // DarkGoldenrod
    "#FF4500", // OrangeRed
    "#32CD32", // LimeGreen
    "#4682B4", // SteelBlue
    "#00FA9A", // MediumSpringGreen
    "#FFD700", // Gold
    "#FF6347", // Tomato
    "#7B68EE", // MediumSlateBlue
    "#008080", // Teal
    "#6A5ACD", // SlateBlue
    "#D2691E", // Chocolate
    "#B22222", // FireBrick
    "#9ACD32", // YellowGreen
    "#8B008B", // DarkMagenta
    "#00CED1", // DarkTurquoise
    "#FF69B4", // HotPink
    "#1E90FF", // DodgerBlue
    "#40E0D0", // Turquoise
    "#EE82EE", // Violet
  ],
};
